import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, ArrowRight, ArrowLeft } from 'lucide-react';

/**
 * DashboardPilotageSection — Aperçu "Cockpit éditorial confidentiel"
 *
 * Version 2 — Stacked card deck (UI compact, navigable).
 * Les 5 sections (Branding, Business Plan, Social Média, Process, Recrutement)
 * deviennent des cartes empilées que l'utilisateur peut faire défiler horizontalement.
 *
 * Style : Playfair Display serif, bordeaux profond, accent or/rouge.
 * Inspiration : Apple Wallet, Stripe Issuing card stack.
 *
 * Mobile-first.
 */

// Palette éditoriale
const COL = {
  bgDark:    '#1A0808',
  bgMid:     '#2A0E0E',
  border:    'rgba(255, 220, 200, 0.10)',
  text:      '#F4E8DE',
  textMuted: 'rgba(244, 232, 222, 0.55)',
  textDim:   'rgba(244, 232, 222, 0.35)',
  red:       '#C9302C',
  gold:      '#C9A95C',
};

// ─────────────────────────────────────────────────────────────────────────
// SECTIONS DATA — Calque sur le pitch deck client réel
// ─────────────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'branding',
    num: '01',
    title: 'Branding',
    accent: COL.red,
    modules: [
      'ADN · Positionnement · Histoire',
      'Identité packaging · Mockups HD',
      'Direction artistique tenues + merch',
    ],
  },
  {
    id: 'business',
    num: '02',
    title: 'Business Plan',
    accent: COL.gold,
    modules: [
      'Prévisionnel 12 mois · 3 scénarios',
      'Plan de financement · ROI · marge',
      'Food cost / prime cost analytique',
    ],
  },
  {
    id: 'social',
    num: '03',
    title: 'Social Média',
    accent: COL.gold,
    modules: [
      'Stratégie 360 · 4 piliers · 90 jours',
      'Plan éditorial · KPIs · budget',
      'Films pub 30s — briefs cinematic',
    ],
  },
  {
    id: 'process',
    num: '04',
    title: 'Process',
    accent: COL.gold,
    modules: [
      'Fiches techniques plats · CRUD',
      'Live food cost par plat',
      'Constitution : 10 commandements',
    ],
  },
  {
    id: 'recrutement',
    num: '05',
    title: 'Recrutement',
    accent: COL.red,
    modules: [
      '7 fiches de poste · 15 recrutements',
      'Onboarding · briefings types',
      'Charte managériale Groupe',
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────
// HEADER — Page de garde "ACCÈS CONFIDENTIEL"
// ─────────────────────────────────────────────────────────────────────────
const DossierHeader = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    className="text-center mb-8"
    data-testid="dossier-header"
  >
    <div className="flex items-center justify-center gap-3 mb-5">
      <span className="w-8 h-px" style={{ background: COL.textDim }} />
      <span className="text-[10px] uppercase tracking-[4px]" style={{ color: COL.textMuted }}>
        Accès confidentiel
      </span>
      <span className="w-8 h-px" style={{ background: COL.textDim }} />
    </div>

    <h2
      className="leading-none mb-3 flex items-baseline justify-center gap-2 sm:gap-3"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      <span className="text-4xl sm:text-6xl" style={{ color: COL.text }}>
        PRSNL
      </span>
      <span className="text-2xl sm:text-3xl" style={{ color: COL.red }}>·</span>
      <span className="text-3xl sm:text-5xl italic" style={{ color: COL.gold }}>
        90
      </span>
    </h2>

    <p className="text-[10px] sm:text-xs uppercase tracking-[5px]" style={{ color: COL.textMuted }}>
      Cockpit de pilotage
    </p>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────
// STACKED CARD DECK
// ─────────────────────────────────────────────────────────────────────────
const CardDeck = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = next, -1 = prev

  const total = SECTIONS.length;

  const goNext = () => {
    setDirection(1);
    setActiveIdx((prev) => (prev + 1) % total);
  };
  const goPrev = () => {
    setDirection(-1);
    setActiveIdx((prev) => (prev - 1 + total) % total);
  };

  // Build the visible stack — front card + 2 cards peeking behind
  const visibleCards = [0, 1, 2].map((offset) => {
    const idx = (activeIdx + offset) % total;
    return { ...SECTIONS[idx], stackPos: offset };
  });

  return (
    <div
      className="relative w-full mx-auto"
      style={{ maxWidth: 420 }}
      data-testid="dossier-card-deck"
    >
      {/* Card stack container (height tied to the front card) */}
      <div className="relative h-[340px] sm:h-[360px]">
        {visibleCards.map((sec) => {
          const isFront = sec.stackPos === 0;
          // Back cards shrink + offset down + fade
          const yOffset = sec.stackPos * 14;
          const scale = 1 - sec.stackPos * 0.04;
          const opacity = 1 - sec.stackPos * 0.25;
          const zIndex = 10 - sec.stackPos;

          return (
            <motion.div
              key={`${sec.id}-${activeIdx}`}
              initial={isFront ? { opacity: 0, y: -20 * direction, scale: 0.96 } : false}
              animate={{
                y: yOffset,
                scale,
                opacity,
              }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-0 top-0 rounded-2xl border p-6 sm:p-7 backdrop-blur-md flex flex-col"
              style={{
                background: isFront
                  ? 'linear-gradient(180deg, #2A1010 0%, #1A0808 100%)'
                  : 'linear-gradient(180deg, rgba(255,220,200,0.05) 0%, rgba(0,0,0,0.45) 100%)',
                borderColor: COL.border,
                zIndex,
                boxShadow: isFront
                  ? '0 24px 60px -16px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,169,92,0.05) inset'
                  : '0 12px 30px -10px rgba(0,0,0,0.40)',
                pointerEvents: isFront ? 'auto' : 'none',
                bottom: 0,
              }}
              data-testid={isFront ? `dossier-active-card-${sec.id}` : undefined}
            >
              {/* Red/gold side bar */}
              <div
                className="absolute left-0 top-6 bottom-6 w-[3px] rounded-r"
                style={{ background: sec.accent }}
              />

              {/* Top row : eyebrow + counter */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p
                    className="text-[10px] uppercase tracking-[3px] font-bold mb-1"
                    style={{ color: COL.textMuted }}
                  >
                    Section {sec.num}
                  </p>
                  <h3
                    className="leading-none text-3xl sm:text-4xl"
                    style={{
                      color: COL.text,
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 400,
                    }}
                  >
                    {sec.title}
                  </h3>
                </div>

                {/* BIENTÔT badge */}
                <span
                  className="px-2 py-1 text-[9px] font-bold uppercase tracking-[1.5px] border rounded-sm"
                  style={{ borderColor: COL.gold, color: COL.gold }}
                >
                  Bientôt
                </span>
              </div>

              {/* Hairline */}
              <div className="h-px my-4" style={{ background: COL.border }} />

              {/* Module list */}
              <ul className="space-y-2.5 flex-1">
                {sec.modules.map((m, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[13px] sm:text-sm leading-snug"
                    style={{ color: COL.textMuted }}
                  >
                    <span
                      className="flex-shrink-0 mt-2 w-1 h-1 rounded-full"
                      style={{ background: sec.accent }}
                    />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>

              {/* Footer step indicator */}
              <div
                className="mt-4 pt-3 border-t flex items-center justify-between"
                style={{ borderColor: COL.border }}
              >
                <span
                  className="text-[10px] uppercase tracking-[3px] font-bold"
                  style={{ color: COL.textDim }}
                >
                  {String(activeIdx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                </span>
                <span
                  className="text-[10px] uppercase tracking-[3px]"
                  style={{ color: COL.textDim }}
                >
                  {sec.modules.length} modules
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Navigation row : prev / dots / next */}
      <div className="flex items-center justify-between mt-6 px-1">
        <button
          type="button"
          onClick={goPrev}
          className="w-10 h-10 rounded-full border flex items-center justify-center transition-colors"
          style={{
            borderColor: COL.border,
            color: COL.textMuted,
          }}
          aria-label="Section précédente"
          data-testid="dossier-prev"
          onMouseEnter={(e) => (e.currentTarget.style.color = COL.text)}
          onMouseLeave={(e) => (e.currentTarget.style.color = COL.textMuted)}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
        </button>

        {/* Dots */}
        <div className="flex items-center gap-2" data-testid="dossier-dots">
          {SECTIONS.map((s, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setDirection(i > activeIdx ? 1 : -1);
                  setActiveIdx(i);
                }}
                aria-label={`Aller à ${s.title}`}
                data-testid={`dossier-dot-${s.id}`}
                className="h-[3px] rounded-full transition-all duration-300"
                style={{
                  width: isActive ? 24 : 10,
                  background: isActive ? s.accent : 'rgba(244,232,222,0.20)',
                }}
              />
            );
          })}
        </div>

        <button
          type="button"
          onClick={goNext}
          className="w-10 h-10 rounded-full border flex items-center justify-center transition-colors"
          style={{
            borderColor: COL.border,
            color: COL.textMuted,
          }}
          aria-label="Section suivante"
          data-testid="dossier-next"
          onMouseEnter={(e) => (e.currentTarget.style.color = COL.text)}
          onMouseLeave={(e) => (e.currentTarget.style.color = COL.textMuted)}
        >
          <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────
export const DashboardPilotageSection = () => {
  return (
    <section
      className="relative overflow-hidden px-4 sm:px-6 py-14 sm:py-16"
      style={{
        background: `linear-gradient(180deg, ${COL.bgDark} 0%, ${COL.bgMid} 50%, ${COL.bgDark} 100%)`,
      }}
      data-testid="dashboard-pilotage-section"
    >
      {/* Film grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Red vignette */}
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none blur-3xl opacity-40"
        style={{
          background: 'radial-gradient(circle, rgba(201,48,44,0.25) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-2xl mx-auto">
        <DossierHeader />

        {/* Access pill */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mx-auto max-w-sm rounded-full border px-4 py-2 flex items-center gap-2 mb-8"
          style={{
            borderColor: COL.border,
            background: 'rgba(0,0,0,0.25)',
          }}
          data-testid="dossier-access-pill"
        >
          <Lock className="w-3 h-3" style={{ color: COL.red }} strokeWidth={2.2} />
          <span
            className="text-[10px] uppercase tracking-[3px] font-bold flex-1 text-center"
            style={{ color: COL.textMuted }}
          >
            Accès délivré à la signature · 1 client = 1 dossier
          </span>
        </motion.div>

        <CardDeck />

        {/* Editorial footer line */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-10 italic text-[12px] sm:text-sm leading-relaxed max-w-md mx-auto"
          style={{ color: COL.textDim, fontFamily: "'Playfair Display', serif" }}
          data-testid="dossier-footer"
        >
          Cockpit déployé pour chaque client{' '}
          <span style={{ color: COL.text }}>Refonte</span>,{' '}
          <span style={{ color: COL.text }}>Ouverture</span> et{' '}
          <span style={{ color: COL.text }}>Groupe</span>.
        </motion.p>
      </div>
    </section>
  );
};

export default DashboardPilotageSection;
